import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus, RefreshCw, Search } from 'lucide-react';

const API_BASE =
  import.meta.env.PROD
    ? 'https://api.clarityofconcept.com/api'
    : (import.meta.env.VITE_LESSON_API_URL || 'http://localhost:8000/api');

type LessonStatus = 'draft' | 'published';

interface LessonSummary {
  lesson_id: number;
  title: string;
  status: LessonStatus;
  version?: number | null;
  subject_id?: number | null;
  subject_name?: string | null;
  category?: string | null;
  access_level?: string | null;
  curriculum_type?: string | null;
  class_level?: number | null;
  chapter_title?: string | null;
  difficulty?: string | null;
  labels?: string[] | null;
  applicable_years?: number[] | null;
  applies_until_replaced?: boolean | number | null;
  page_count?: number | null;
  block_count?: number | null;
  updated_at?: string | null;
  created_at?: string | null;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const statusChip = (status: LessonStatus) => {
  const base =
    'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold';
  if (status === 'published') {
    return <span className={`${base} bg-green-100 text-green-800`}>Published</span>;
  }
  return <span className={`${base} bg-amber-100 text-amber-800`}>Draft</span>;
};

const difficultyLabel: Record<string, string> = {
  beginner: 'Beginner',
  medium: 'Medium',
  advanced: 'Advanced',
};

const curriculumLabel = (value?: string | null) => {
  switch ((value || 'general').toLowerCase()) {
    case 'ncert':
      return 'NCERT';
    case 'icse':
      return 'ICSE';
    case 'misc':
      return 'Misc';
    default:
      return 'General';
  }
};

const categoryLabel = (value?: string | null) => {
  switch ((value || 'static').toLowerCase()) {
    case 'current_affairs':
      return 'Current affairs';
    case 'both':
      return 'Static + Current';
    default:
      return 'Static';
  }
};

const accessLabel = (value?: string | null) => {
  return (value || 'free').toLowerCase() === 'premium' ? 'Premium' : 'Free';
};

const LessonsLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<LessonSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | LessonStatus>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredCountLabel = useMemo(() => {
    if (statusFilter === 'all') return `${total} total`;
    const count = lessons.filter((l) => l.status === statusFilter).length;
    return `${count} ${statusFilter}`;
  }, [lessons, statusFilter, total]);

  const fetchLessons = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('limit', '200');
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search.trim()) params.append('search', search.trim());
      const res = await fetch(`${API_BASE}/lessons?${params.toString()}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to load lessons (${res.status})`);
      }
      const data = await res.json();
      setLessons(data.lessons || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      console.error('Failed to load lessons', err);
      setError(err?.message || 'Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLessons();
  };

  const emptyState = !loading && lessons.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-50">Lessons</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Browse drafts, published lessons, and jump back into editing.
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{filteredCountLabel}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchLessons}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button
            onClick={() => navigate('/lessons/new')}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New lesson
          </button>
        </div>
      </div>

      <div className="border border-gray-200 dark:border-gray-800 rounded-3xl bg-white dark:bg-gray-900 p-4 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-800 p-1">
            {(['all', 'draft', 'published'] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => setStatusFilter(opt)}
                className={`px-3 py-1 text-sm rounded-full ${
                  statusFilter === opt
                    ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-50'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                {opt === 'all' ? 'All' : opt === 'draft' ? 'Drafts' : 'Published'}
              </button>
            ))}
          </div>
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 w-full md:w-auto md:min-w-[320px]">
            <div className="flex-1 flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent text-sm focus:outline-none"
                placeholder="Search by title or subject"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm font-semibold hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              Apply
            </button>
          </form>
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {emptyState ? (
          <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl p-8 text-center space-y-2">
            <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">No lessons yet</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Create your first lesson to start building page-by-page content.
            </div>
            <button
              onClick={() => navigate('/lessons/new')}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Create lesson
            </button>
          </div>
        ) : (
          <div className="overflow-auto rounded-2xl border border-gray-200 dark:border-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/70">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Title</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Subject</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Track</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Content type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Access</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Difficulty</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Pages</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Version</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Updated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {lessons.map((lesson) => (
                  <tr key={lesson.lesson_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900 dark:text-gray-50">{lesson.title || 'Untitled'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-2 items-center flex-wrap">
                        <span>#{lesson.lesson_id}</span>
                        {lesson.labels?.length ? (
                          <span className="truncate">
                            {lesson.labels.slice(0, 2).join(', ')}
                            {lesson.labels.length > 2 ? '…' : ''}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      {lesson.subject_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {curriculumLabel(lesson.curriculum_type)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {lesson.class_level
                          ? `Class ${lesson.class_level} - ${lesson.chapter_title || '—'}`
                          : lesson.chapter_title || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      {categoryLabel(lesson.category)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                        accessLabel(lesson.access_level) === 'Premium'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {accessLabel(lesson.access_level)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      {difficultyLabel[lesson.difficulty || 'beginner'] || 'Beginner'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      {lesson.page_count ?? 0}
                      {lesson.block_count ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400"> ({lesson.block_count} blocks)</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">v{lesson.version ?? 0}</td>
                    <td className="px-4 py-3">{statusChip(lesson.status)}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200 whitespace-nowrap">
                      {formatDateTime(lesson.updated_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`/lessons/${lesson.lesson_id}`)}
                        className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonsLibrary;
